import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle
} from '@headlessui/react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import React, { useRef, useState } from 'react'

import { usePrintState } from '@/stores/usePrintStore'

interface PrintDialogProps {
  children?: React.ReactNode
  visible: boolean
  isMultiPage?: boolean
  pageWidth: number
  pageHeight?: number
  pageCssContent?: string
  continuousContent?: React.ReactNode
  onCancel: () => void
}

const PrintDialog: React.FC<PrintDialogProps> = ({
  children,
  visible,
  isMultiPage = false,
  pageWidth,
  pageHeight = null,
  pageCssContent,
  onCancel
}) => {
  const printRef = useRef<HTMLDivElement>(null)
  const { print, printer } = usePrintState()
  const [currentPage, setCurrentPage] = useState(0)
  const pageCount = isMultiPage ? React.Children.count(children) : 1
  const pages = isMultiPage ? React.Children.toArray(children) : [children]

  const handlePrint = async () => {
    if (!printRef.current) return

    try {
      if (isMultiPage) {
        // 分页打印模式：按顺序打印每一页
        const pageContents: string[] = []
        const originalPage = currentPage

        for (let i = 0; i < pages.length; i++) {
          // 设置当前页面
          setCurrentPage(i)

          // 等待React重新渲染
          await new Promise((resolve) => setTimeout(resolve, 100))

          // 获取当前页面的HTML内容
          const htmlContent = `
            <!DOCTYPE html>
            <html>
              <head>
                <title>标签打印 - 第${i + 1}页</title>
                <style>${pageCssContent}</style>
              </head>
              <body>
                ${printRef.current.innerHTML}
              </body>
            </html>
          `
          pageContents.push(htmlContent)
        }

        // 恢复原来的页面
        setCurrentPage(originalPage)

        // 使用打印工具的多页打印功能
        await print?.printToHTML(printer, pageContents, {
          width: pageWidth,
          height: pageHeight
        })
      } else {
        // 单页打印
        const htmlContent = `
          <!DOCTYPE html>
          <html>
            <head>
              <title>标签打印</title>
              <style>${pageCssContent}</style>
            </head>
            <body>
              ${printRef.current.innerHTML}
            </body>
          </html>
        `
        await print?.printToHTML(printer, htmlContent, {
          width: pageWidth,
          height: pageHeight
        })
      }

      onCancel()
    } catch (error) {
      console.error('QZ打印失败:', error)
      // 回退到浏览器打印
      const allContent = isMultiPage
        ? pages
            .map((page) => {
              const wrapper = document.createElement('div')
              wrapper.innerHTML = typeof page === 'string' ? page : ''
              return wrapper.innerHTML
            })
            .join('<div class="page-break"></div>')
        : printRef.current?.innerHTML || ''

      const htmlContent = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>标签打印</title>
            <style>${pageCssContent}</style>
          </head>
          <body>
            ${allContent}
          </body>
        </html>
      `

      const printWindow = window.open('', '_blank')
      if (printWindow) {
        printWindow.document.documentElement.innerHTML = htmlContent
        printWindow.print()
        printWindow.close()
      }
    }
  }

  return (
    <Dialog
      open={visible}
      onClose={onCancel}
      className="relative z-50"
      transition
    >
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-black/50 duration-300 ease-out data-[closed]:opacity-0"
      />

      <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
        <DialogPanel
          transition
          className="w-auto max-h-[90vh] transform overflow-hidden rounded-lg bg-white text-left align-middle shadow-xl duration-300 ease-out data-[closed]:scale-95 data-[closed]:opacity-0 flex flex-col"
        >
          <div className="p-6 pb-4 flex-1">
            <DialogTitle className="flex items-center justify-between mb-4">
              <span className="text-xl font-bold text-gray-900">
                打印预览
                {isMultiPage && `(${currentPage + 1}/${pageCount})`}
              </span>
              <div className="flex space-x-2">
                {/* 分页导航 */}
                {isMultiPage && (
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() =>
                        setCurrentPage(Math.max(0, currentPage - 1))
                      }
                      disabled={currentPage === 0}
                      className="p-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() =>
                        setCurrentPage(Math.min(pageCount - 1, currentPage + 1))
                      }
                      disabled={currentPage === pageCount - 1}
                      className="p-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                )}

                <button
                  onClick={onCancel}
                  className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  关闭
                </button>
                <button
                  onClick={handlePrint}
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  打印
                </button>
              </div>
            </DialogTitle>

            {/* 预览容器 */}
            <div className="flex justify-center">
              <div
                className="bg-white border-2 border-gray-400"
                style={{
                  width: `${pageWidth}mm`,
                  height: pageHeight === null ? 'auto' : `${pageHeight}mm`,
                  maxHeight: pageHeight === null ? 'auto' : `${pageHeight}mm`,
                  overflow: pageHeight === null ? 'auto' : 'hidden'
                }}
                ref={printRef}
              >
                {isMultiPage ? pages[currentPage] : children}
              </div>
            </div>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  )
}

export default PrintDialog
